import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";

// Loosely typed to match how pages already consume the athlete (untyped useQuery data).
// `id` stays typed; other fields are `any` to match how pages already consume the
// athlete (the old useQuery data was untyped), so migrating them adds no type errors.
export type Athlete = { id: string } & Record<string, any>;

type ActiveAthleteValue = {
  athletes: Athlete[];
  athlete: Athlete | null; // the active athlete (falls back to the primary), null if none
  activeAthleteId: string | null;
  setActiveAthleteId: (id: string) => void;
  isLoading: boolean;
};

const ActiveAthleteContext = createContext<ActiveAthleteValue>({
  athletes: [],
  athlete: null,
  activeAthleteId: null,
  setActiveAthleteId: () => {},
  isLoading: false,
});

const LS_KEY = "legacyloop_active_athlete_id";

function readStored(): string | null {
  try {
    return localStorage.getItem(LS_KEY);
  } catch {
    return null;
  }
}
function writeStored(id: string) {
  try {
    localStorage.setItem(LS_KEY, id);
  } catch {
    /* private mode / storage blocked — active athlete just resets to primary */
  }
}

/**
 * Shared "which athlete am I viewing" state for the whole app. Fetches the user's
 * athletes once (GET /api/athletes), tracks the active one in localStorage, and
 * falls back to the primary (oldest) when nothing is stored or the stored id is gone.
 * Pages read the active athlete via useActiveAthlete() instead of querying
 * /api/athletes/me directly, so switching athletes re-keys every athlete-scoped query.
 */
export function ActiveAthleteProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const { data, isLoading } = useQuery<Athlete[]>({
    queryKey: ["/api/athletes"],
    enabled: isAuthenticated,
  });
  const athletes = Array.isArray(data) ? data : [];

  const [storedId, setStoredId] = useState<string | null>(() => readStored());

  const athlete = useMemo(() => {
    if (athletes.length === 0) return null;
    return athletes.find((a) => a.id === storedId) ?? athletes[0];
  }, [athletes, storedId]);

  // Keep the stored id aligned with the resolved athlete (handles first load and a
  // stored id that no longer exists).
  useEffect(() => {
    if (athlete && athlete.id !== storedId) {
      setStoredId(athlete.id);
      writeStored(athlete.id);
    }
  }, [athlete, storedId]);

  const setActiveAthleteId = (id: string) => {
    setStoredId(id);
    writeStored(id);
  };

  const value = useMemo<ActiveAthleteValue>(
    () => ({
      athletes,
      athlete,
      activeAthleteId: athlete?.id ?? null,
      setActiveAthleteId,
      isLoading,
    }),
    [athletes, athlete, isLoading],
  );

  return <ActiveAthleteContext.Provider value={value}>{children}</ActiveAthleteContext.Provider>;
}

export function useActiveAthlete() {
  return useContext(ActiveAthleteContext);
}
